import React, { useState, useEffect, useRef } from "react";
import Peer from "simple-peer";
import FALLBACK_ICE_SERVERS, { fetchIceServers } from "../../utils/iceServers";

export default function CalleeVideoOverlay({ socket, callerSocketId, callerUsername, callType = "video", onEnd }) {
  const [callStatus, setCallStatus] = useState("connecting");
  const isVideoCall = callType === "video";
  const [isMuted, setIsMuted] = useState(false);
  const [isCamOff, setIsCamOff] = useState(false);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerRef = useRef(null);
  const streamRef = useRef(null);
  const mountedRef = useRef(true);
  const pendingSignals = useRef([]);
  const iceServersRef = useRef(FALLBACK_ICE_SERVERS); // Buffer signals that arrive before media is ready

  useEffect(() => {
    mountedRef.current = true;
    let mediaReady = false;

    // Fetch TURN credentials
    fetchIceServers().then((servers) => { iceServersRef.current = servers; });

    function processSignal({ from, signal }) {
      if (!mountedRef.current) return;

      if (!mediaReady || !streamRef.current) {
        // Buffer the signal until media is ready
        pendingSignals.current.push({ from, signal });
        return;
      }

      handleSignal(from, signal);
    }

    function handleSignal(from, signal) {
      if (peerRef.current) {
        peerRef.current.signal(signal);
      } else {
        // First signal — create answering peer
        setCallStatus("connected");
        const peer = new Peer({ initiator: false, trickle: true, stream: streamRef.current, config: { iceServers: iceServersRef.current } });

        peer.on("signal", (data) => {
          socket.emit("webrtc-signal", { target: from, signal: data });
        });

        peer.on("stream", (remoteStream) => {
          if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
        });

        peer.on("close", () => { if (mountedRef.current) setCallStatus("failed"); });
        peer.on("error", (err) => { console.error("Callee peer error:", err); if (mountedRef.current) setCallStatus("failed"); });

        peerRef.current = peer;
        peer.signal(signal);
      }
    }

    function onPartnerDisconnected() { if (mountedRef.current) setCallStatus("failed"); }
    function onCallEnded() { if (mountedRef.current) setCallStatus("failed"); }

    socket.on("webrtc-signal", processSignal);
    socket.on("partnerDisconnected", onPartnerDisconnected);
    socket.on("callEnded", onCallEnded);

    // Get media, then flush any buffered signals
    (async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({ video: isVideoCall, audio: true });
        if (!mountedRef.current) { mediaStream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = mediaStream;
        if (localVideoRef.current) localVideoRef.current.srcObject = mediaStream;
        mediaReady = true;

        // Process any signals that arrived while we were getting media
        const buffered = pendingSignals.current.splice(0);
        for (const { from, signal } of buffered) {
          handleSignal(from, signal);
        }
      } catch {
        if (mountedRef.current) setCallStatus("failed");
      }
    })();

    return () => {
      mountedRef.current = false;
      socket.off("webrtc-signal", processSignal);
      socket.off("partnerDisconnected", onPartnerDisconnected);
      socket.off("callEnded", onCallEnded);
      if (peerRef.current) { peerRef.current.destroy(); peerRef.current = null; }
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); }
    };
  }, [socket, callerSocketId]);

  const handleEndCall = () => {
    if (socket) socket.emit("hangup", { callEnder: socket.id });
    if (peerRef.current) { peerRef.current.destroy(); peerRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    onEnd();
  };

  const toggleMute = () => {
    if (streamRef.current) {
      streamRef.current.getAudioTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsMuted(p => !p);
    }
  };

  const toggleCam = () => {
    if (streamRef.current) {
      streamRef.current.getVideoTracks().forEach(t => { t.enabled = !t.enabled; });
      setIsCamOff(p => !p);
    }
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "#101010", zIndex: 1500,
      display: "flex", flexDirection: "column",
    }}>
      <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isVideoCall && (
          <video ref={remoteVideoRef} autoPlay playsInline style={{
            width: "100%", height: "100%", objectFit: "cover", position: "absolute", top: 0, left: 0,
            display: callStatus === "connected" ? "block" : "none",
          }} />
        )}
        {!isVideoCall && <audio ref={remoteVideoRef} autoPlay />}

        {(callStatus !== "connected" || !isVideoCall) && (
          <div style={{ textAlign: "center" }}>
            <h3 style={{ color: "#fff", margin: "0 0 8px" }}>{callerUsername}</h3>
            <p style={{ color: "#aaa", fontSize: "0.9rem" }}>
              {callStatus === "connected" && !isVideoCall ? "On call..." : callStatus === "connecting" ? "Connecting..." : "Call ended"}
            </p>
            {callStatus === "failed" && (
              <button onClick={onEnd} style={{
                marginTop: 12, backgroundColor: "rgba(255,255,255,0.1)", color: "#fff",
                border: "1px solid rgba(255,255,255,0.2)", borderRadius: 20, padding: "8px 24px",
                cursor: "pointer", fontSize: "0.9rem",
              }}>Close</button>
            )}
          </div>
        )}

        {isVideoCall && (
          <div style={{
            position: "absolute", bottom: 80, right: 16,
            width: 120, height: 90, borderRadius: 10, overflow: "hidden",
            border: "2px solid rgba(185,151,255,0.4)", boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
          }}>
            <video ref={localVideoRef} autoPlay playsInline muted style={{
              width: "100%", height: "100%", objectFit: "cover", opacity: isCamOff ? 0.3 : 1,
            }} />
          </div>
        )}
      </div>

      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 16, padding: "16px 0", backgroundColor: "rgba(0,0,0,0.6)",
      }}>
        <button onClick={toggleMute} style={{
          width: 48, height: 48, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.15)",
          backgroundColor: isMuted ? "rgba(185,151,255,0.3)" : "rgba(255,255,255,0.1)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ion-icon name={isMuted ? "mic-off" : "mic"} style={{ color: isMuted ? "#ff6666" : "#fff", fontSize: "1.4rem" }}></ion-icon>
        </button>

        {isVideoCall && (
          <button onClick={toggleCam} style={{
            width: 48, height: 48, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.15)",
            backgroundColor: isCamOff ? "rgba(185,151,255,0.3)" : "rgba(255,255,255,0.1)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <ion-icon name={isCamOff ? "videocam-off" : "videocam"} style={{ color: isCamOff ? "#ff6666" : "#fff", fontSize: "1.4rem" }}></ion-icon>
          </button>
        )}

        <button onClick={handleEndCall} style={{
          width: 56, height: 56, borderRadius: "50%", border: "none",
          backgroundColor: "#ff4444", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ion-icon name="call" style={{ color: "#fff", fontSize: "1.6rem", transform: "rotate(135deg)" }}></ion-icon>
        </button>
      </div>
    </div>
  );
}
